import * as THREE from 'three';
import { SimplexNoise } from '../../utils/SimplexNoise.js';

/**
 * Factory for creating pooled objects. Pre-generates geometry variants for
 * procedural objects (rocks) to eliminate expensive per-object noise computation.
 */
export class ObjectFactory {
    /**
     * @param {AssetLoader} assetLoader - Reference to centralized asset loader
     */
    constructor(assetLoader) {
        this.assetLoader = assetLoader;
        this.noise = new SimplexNoise();

        // Configuration loaded from JSON
        this.config = null;

        // Pre-generated geometry variants (typeId -> geometry[])
        this.geometryVariants = new Map();

        // Material cache (key: "typeId_colorHex" -> Material)
        this.materials = new Map();

        // Base rock material - cloned for each rock instance so they can fade independently
        // Color is updated globally via setRockColor()
        this.baseRockMaterial = new THREE.MeshLambertMaterial({
            color: 0x9c8c7c,  // Warm grey-brown, updated by color cycle
            flatShading: true
        });

        // Track all rock materials for color updates (Set prevents duplicates)
        this.rockMaterials = new Set();

        // Shrub textures (loaded once, shared)
        this.shrubTextures = [];

        // Shared billboard geometry (reused for all billboard sprites to reduce GPU memory)
        this.sharedBillboardGeometry = new THREE.PlaneGeometry(1, 1);

        // Billboard material cache (key: "textureIndex_colorHex" -> Material)
        // Reduces material proliferation for shrubs with same texture and color
        this.billboardMaterials = new Map();
    }

    /**
     * Initialize factory with configuration.
     * @param {Object} config - Parsed roadside-objects.json
     */
    async initialize(config) {
        this.config = config;

        // Pre-generate geometry variants for procedural types
        for (const [typeId, typeConfig] of Object.entries(config.types)) {
            if (typeConfig.procedural) {
                await this._preGenerateVariants(typeId, typeConfig);
            }

            // Preload textures for billboard types
            if (typeConfig.billboard && typeConfig.textures) {
                await this._preloadTextures(typeConfig.textures);
            }

            // Preload models
            if (typeConfig.model) {
                await this.assetLoader.loadModel(typeConfig.model);
            }
        }
    }

    /**
     * Create a new object instance for the pool.
     * @param {string} typeId - Type identifier
     * @param {Object} [options] - Creation options (color, etc.)
     * @returns {THREE.Object3D}
     */
    create(typeId, options = {}) {
        const typeConfig = this.config?.types[typeId];
        if (!typeConfig) {
            console.warn(`ObjectFactory: Unknown type "${typeId}"`);
            return null;
        }

        if (typeConfig.procedural) {
            return this._createProcedural(typeId, typeConfig, options);
        } else if (typeConfig.billboard) {
            return this._createBillboard(typeId, typeConfig, options);
        } else if (typeConfig.model) {
            return this._createModel(typeId, typeConfig, options);
        }

        console.warn(`ObjectFactory: No creation method for type "${typeId}"`);
        return null;
    }

    /**
     * Reset an object for reuse from the pool.
     * @param {THREE.Object3D} obj - Object to reset
     * @param {Object} [options] - Reset options (color, etc.)
     */
    reset(obj, options = {}) {
        const typeId = obj.userData._poolTypeId;
        const typeConfig = this.config?.types[typeId];

        // Reset common properties
        obj.visible = false;
        obj.position.set(0, 0, 0);
        obj.rotation.set(0, 0, 0);
        obj.scale.set(1, 1, 1);

        // Reset spawn time for fade-in
        obj.userData.spawnTime = performance.now() / 1000;
        obj.userData.spawned = false;
        obj.userData.markedForRemoval = false;

        // Apply new color if provided (but NOT for rocks - they use shared material)
        if (options.color !== undefined) {
            const isRock = typeConfig?.procedural && typeConfig.proceduralType === 'rock';
            if (!isRock) {
                this._applyColor(obj, typeId, options.color);
            }
        }

        // Apply random transforms based on type
        if (typeConfig?.procedural && typeConfig.proceduralType === 'rock') {
            this._randomizeRock(obj, typeConfig);
        } else if (typeConfig?.billboard) {
            this._randomizeBillboard(obj, typeConfig);
        }
    }

    /**
     * Get or create a shared material for a type/color combination.
     * @param {string} typeId - Type identifier
     * @param {number} color - Color as hex number
     * @returns {THREE.Material}
     */
    getMaterial(typeId, color) {
        const typeConfig = this.config?.types[typeId];

        // Rocks get cloned materials so they can fade independently
        // Color is synced globally via setRockColor()
        if (typeConfig?.procedural && typeConfig.proceduralType === 'rock') {
            const material = this.baseRockMaterial.clone();
            this.rockMaterials.add(material);
            return material;
        }

        const key = `${typeId}_${color.toString(16)}`;

        if (this.materials.has(key)) {
            return this.materials.get(key);
        }

        let material;

        if (typeConfig?.billboard) {
            // Billboard material with texture - use dedicated billboard cache
            const textureData = this._getRandomShrubTexture();
            if (textureData) {
                material = this._getBillboardMaterial(textureData.index, textureData.texture, color);
            } else {
                // Fallback without texture
                material = new THREE.MeshLambertMaterial({
                    color: color,
                    transparent: true,
                    side: THREE.DoubleSide
                });
            }
        } else {
            // Default material
            material = new THREE.MeshLambertMaterial({ color: color });
        }

        this.materials.set(key, material);
        return material;
    }

    /**
     * Update all rock materials to a new color.
     * Called by BiomeRenderer during day/night color transitions.
     * @param {number} color - Color as hex number
     */
    setRockColor(color) {
        // Update base material (for new rocks)
        if (this.baseRockMaterial) {
            this.baseRockMaterial.color.setHex(color);
        }
        // Update all existing rock materials
        for (const material of this.rockMaterials) {
            material.color.setHex(color);
        }
    }

    /**
     * Dispose all cached materials and geometries.
     */
    dispose() {
        // Dispose base rock material
        if (this.baseRockMaterial) {
            this.baseRockMaterial.dispose();
            this.baseRockMaterial = null;
        }

        // Dispose all rock materials
        for (const material of this.rockMaterials) {
            material.dispose();
        }
        this.rockMaterials.clear();

        // Dispose other materials
        for (const material of this.materials.values()) {
            material.dispose();
        }
        this.materials.clear();

        // Dispose geometry variants
        for (const variants of this.geometryVariants.values()) {
            for (const geom of variants) {
                geom.dispose();
            }
        }
        this.geometryVariants.clear();

        // Dispose shared billboard geometry
        if (this.sharedBillboardGeometry) {
            this.sharedBillboardGeometry.dispose();
            this.sharedBillboardGeometry = null;
        }

        // Dispose billboard materials
        for (const material of this.billboardMaterials.values()) {
            material.dispose();
        }
        this.billboardMaterials.clear();

        this.shrubTextures = [];
    }

    // ========== Private Methods ==========

    /**
     * Pre-generate geometry variants for a procedural type.
     * @private
     */
    async _preGenerateVariants(typeId, typeConfig) {
        const variantCount = typeConfig.variantCount || 15;
        const variants = [];

        if (typeConfig.proceduralType === 'rock') {
            for (let i = 0; i < variantCount; i++) {
                const geom = this._generateRockGeometry(typeConfig);
                variants.push(geom);
            }
        }

        this.geometryVariants.set(typeId, variants);
    }

    /**
     * Generate a single rock geometry with noise deformation.
     * @private
     */
    _generateRockGeometry(typeConfig) {
        const sizeRange = typeConfig.sizeRange || [2, 7];
        // Use unit size, actual scale applied at acquire time
        const size = 1;

        const detail = 2; // ~80 faces
        const geometry = new THREE.IcosahedronGeometry(size, detail);

        // Random seed for unique noise
        const seedX = Math.random() * 1000;
        const seedY = Math.random() * 1000;
        const seedZ = Math.random() * 1000;

        const positions = geometry.attributes.position;
        for (let i = 0; i < positions.count; i++) {
            const vx = positions.getX(i);
            const vy = positions.getY(i);
            const vz = positions.getZ(i);

            const length = Math.sqrt(vx * vx + vy * vy + vz * vz);
            const nx = vx / length;
            const ny = vy / length;
            const nz = vz / length;

            // Multi-octave noise
            const noiseScale1 = 1.5;
            const noiseScale2 = 3.0;
            const noiseScale3 = 6.0;

            const noise1 = this.noise.noise3D(nx * noiseScale1 + seedX, ny * noiseScale1 + seedY, nz * noiseScale1 + seedZ);
            const noise2 = this.noise.noise3D(nx * noiseScale2 + seedX, ny * noiseScale2 + seedY, nz * noiseScale2 + seedZ);
            const noise3 = this.noise.noise3D(nx * noiseScale3 + seedX, ny * noiseScale3 + seedY, nz * noiseScale3 + seedZ);

            const combinedNoise = noise1 * 0.5 + noise2 * 0.3 + noise3 * 0.2;
            const displacement = 0.9 + combinedNoise * 0.3;
            const newLength = size * displacement;

            positions.setX(i, nx * newLength);
            positions.setY(i, ny * newLength);
            positions.setZ(i, nz * newLength);
        }

        positions.needsUpdate = true;
        geometry.computeVertexNormals();

        return geometry;
    }

    /**
     * Preload textures for billboard types.
     * @private
     */
    async _preloadTextures(texturePaths) {
        for (const path of texturePaths) {
            try {
                const texture = await this.assetLoader.loadTexture(path);
                this.shrubTextures.push(texture);
            } catch (e) {
                console.warn(`ObjectFactory: Failed to load texture ${path}`);
            }
        }
    }

    /**
     * Get a random shrub texture from preloaded textures.
     * @private
     * @returns {{ texture: THREE.Texture, index: number } | null}
     */
    _getRandomShrubTexture() {
        if (this.shrubTextures.length === 0) return null;
        const index = Math.floor(Math.random() * this.shrubTextures.length);
        return { texture: this.shrubTextures[index], index };
    }

    /**
     * Get or create a cached billboard material for the given texture and color.
     * @private
     * @param {number} textureIndex - Index of the texture in shrubTextures array
     * @param {THREE.Texture} texture - The texture to use
     * @param {number} color - Color as hex number
     * @returns {THREE.Material}
     */
    _getBillboardMaterial(textureIndex, texture, color) {
        const key = `${textureIndex}_${color.toString(16)}`;

        if (this.billboardMaterials.has(key)) {
            return this.billboardMaterials.get(key);
        }

        const material = new THREE.MeshLambertMaterial({
            map: texture,
            transparent: true,
            alphaTest: 0.5,
            side: THREE.DoubleSide,
            color: color,
            emissive: color,
            emissiveIntensity: 0.3
        });

        this.billboardMaterials.set(key, material);
        return material;
    }

    /**
     * Create a procedural object (rock).
     * @private
     */
    _createProcedural(typeId, typeConfig, options) {
        const variants = this.geometryVariants.get(typeId);
        if (!variants || variants.length === 0) {
            console.warn(`ObjectFactory: No variants for ${typeId}`);
            return null;
        }

        // Pick random variant
        const geometry = variants[Math.floor(Math.random() * variants.length)];
        const color = options.color || 0x888888;
        const material = this.getMaterial(typeId, color);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        mesh.userData.type = typeId;
        mesh.userData.spawnTime = performance.now() / 1000;
        mesh.userData.spawned = false;

        // Apply initial random transforms
        this._randomizeRock(mesh, typeConfig);

        return mesh;
    }

    /**
     * Apply random rotation and scale to a rock.
     * @private
     */
    _randomizeRock(mesh, typeConfig) {
        const sizeRange = typeConfig.sizeRange || [2, 7];
        const size = sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]);

        // Random rotation
        mesh.rotation.x = Math.random() * Math.PI * 2;
        mesh.rotation.y = Math.random() * Math.PI * 2;
        mesh.rotation.z = Math.random() * Math.PI * 2;

        // Non-uniform scale for natural shapes
        const baseScale = size * (0.7 + Math.random() * 0.6);
        mesh.scale.set(
            baseScale * (0.8 + Math.random() * 0.4),
            baseScale * (0.6 + Math.random() * 0.5), // Flatter
            baseScale * (0.8 + Math.random() * 0.4)
        );
    }

    /**
     * Create a billboard object (shrub).
     * @private
     */
    _createBillboard(typeId, typeConfig, options) {
        const spriteCount = typeConfig.spriteCount || 3;
        const group = new THREE.Group();

        const color = options.color || 0x228822;

        for (let i = 0; i < spriteCount; i++) {
            const textureData = this._getRandomShrubTexture();
            let material;

            if (textureData) {
                // Use cached material to reduce material proliferation
                material = this._getBillboardMaterial(textureData.index, textureData.texture, color);
            } else {
                material = new THREE.MeshLambertMaterial({
                    color: color,
                    transparent: true,
                    side: THREE.DoubleSide
                });
            }

            // Billboard plane (reuse shared geometry to reduce GPU memory)
            const billboard = new THREE.Mesh(this.sharedBillboardGeometry, material);

            billboard.userData.baseRotationY = Math.random() * Math.PI * 2;
            group.add(billboard);
        }

        group.userData.type = typeId;
        group.userData.spawnTime = performance.now() / 1000;
        group.userData.spawned = false;

        this._randomizeBillboard(group, typeConfig);

        return group;
    }

    /**
     * Apply random transforms to billboard (shrub).
     * @private
     */
    _randomizeBillboard(group, typeConfig) {
        const sizeRange = typeConfig.sizeRange || [3, 8];
        const size = sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]);
        const scaleVariation = typeConfig.scaleVariation || 0.3;
        const stretchVariation = typeConfig.stretchVariation || 0.3;

        group.children.forEach((billboard) => {
            const baseScale = size * (0.6 + Math.random() * 0.8);
            const scaleVar = 1.0 - scaleVariation + Math.random() * (scaleVariation * 2);
            const stretchFactor = 1.0 - stretchVariation + Math.random() * (stretchVariation * 2);
            const width = baseScale * scaleVar;
            const height = baseScale * scaleVar * stretchFactor;

            billboard.scale.set(width, height, 1);

            // Offset within cluster
            const offsetX = (Math.random() - 0.5) * size * 0.5;
            const offsetY = height * 0.5 - 1;
            const offsetZ = (Math.random() - 0.5) * size * 0.5;
            billboard.position.set(offsetX, offsetY, offsetZ);

            billboard.userData.baseRotationY = Math.random() * Math.PI * 2;
        });
    }

    /**
     * Create a model-based object.
     * @private
     */
    _createModel(typeId, typeConfig, options) {
        const model = this.assetLoader.cloneModel(typeConfig.model);
        if (!model) {
            console.warn(`ObjectFactory: Model not loaded for ${typeId}`);
            return null;
        }

        model.userData.type = typeId;
        model.userData.spawnTime = performance.now() / 1000;
        model.userData.spawned = false;

        return model;
    }

    /**
     * Apply color to an object.
     * @private
     */
    _applyColor(obj, typeId, color) {
        const material = this.getMaterial(typeId, color);

        if (obj.isMesh) {
            obj.material = material;
        } else if (obj.isGroup) {
            obj.traverse((child) => {
                if (child.isMesh) {
                    // For billboards, use cached material with texture
                    const typeConfig = this.config?.types[typeId];
                    if (typeConfig?.billboard) {
                        const textureData = this._getRandomShrubTexture();
                        if (textureData) {
                            // Use cached material to reduce material proliferation
                            child.material = this._getBillboardMaterial(textureData.index, textureData.texture, color);
                        }
                    } else {
                        child.material = material;
                    }
                }
            });
        }
    }
}
